'use client'

import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import type clsx from 'clsx'
import type { ReactNode } from 'react'

import { useTopNavV5 } from '@src/components/TopNavV5/TopNavV5Context'
import { TopNavV5MobileCloseHeader } from '@src/components/TopNavV5/TopNavV5Mobile/TopNavV5MobileCloseHeader'
import { TopNavV5MobileLeftMenu } from '@src/components/TopNavV5/TopNavV5Mobile/TopNavV5MobileLeftMenu'
import { TopNavV5MobileMenu } from '@src/components/TopNavV5/TopNavV5Mobile/TopNavV5MobileMenu'
import { TopNavV5MobileSubMenu } from '@src/components/TopNavV5/TopNavV5Mobile/TopNavV5MobileSubMenu'
import { useBreakpoints } from '@src/utils/breakpoints.util'
import { cn } from '@src/utils/tailwind'

const asideClassNames =
  'size-full bg-bodi-black-text bg-linear-to-t from-white/10 to-white/0 text-white z-max duration-200 transition-all transition ease-out flex flex-col'

const Panel = ({
  open,
  onClose,
  className,
  content,
}: {
  open: boolean
  onClose: () => void
  className?: clsx.ClassValue
  content: ReactNode
}) => {
  return (
    <Dialog
      transition
      className="relative z-max transition duration-200 ease-out"
      onClose={onClose}
      open={open}
      static
    >
      <Transition show={open} as="div" className="fixed inset-0">
        <TransitionChild>
          <DialogPanel className={cn(asideClassNames, className)}>
            <TopNavV5MobileCloseHeader onClick={onClose} />
            <div className="flex-1 overflow-y-auto">{content}</div>
          </DialogPanel>
        </TransitionChild>
      </Transition>
    </Dialog>
  )
}

export const TopNavV5MobileSlidingPanel = () => {
  const { mobileState, mobileDispatch } = useTopNavV5()
  const { isDesktop } = useBreakpoints()

  if (isDesktop) return null

  const renderContent = () => {
    return (
      <>
        {/* level 1 - menu items */}
        <Panel
          open={mobileState.state === 'open'}
          onClose={() => mobileDispatch({ type: 'CLOSE_FROM_MAIN' })}
          content={<TopNavV5MobileMenu />}
          className="translate-x-0 data-closed:-translate-x-full"
        />

        {/* level 2 - sub menu or left menu */}
        <Panel
          open={mobileState.state === 'sub-menu-open'}
          onClose={() => mobileDispatch({ type: 'CLOSE_FROM_SUB_MENU' })}
          content={<TopNavV5MobileSubMenu />}
          className={{
            'data-closed:translate-x-full': true,
            'data-closed:-translate-x-full': mobileState.state === 'closed',
          }}
        />
        <Panel
          open={mobileState.state === 'left-menu-open'}
          onClose={() => mobileDispatch({ type: 'CLOSE_FROM_LEFT_MENU' })}
          content={<TopNavV5MobileLeftMenu />}
          className={{
            'data-closed:translate-x-full': true,
            'data-closed:-translate-x-full':
              mobileState.state === 'closed' ||
              (mobileState.state === 'left-menu-open' &&
                mobileState.previousState === 'left-menu-sub-menu-open') ||
              mobileState.state === 'left-menu-sub-menu-open',
          }}
        />

        {/* level 3 - left menu sub menu*/}
        <Panel
          open={mobileState.state === 'left-menu-sub-menu-open'}
          onClose={() => mobileDispatch({ type: 'CLOSE_FROM_LEFT_MENU_SUB_MENU' })}
          content={<TopNavV5MobileSubMenu />}
          className={{
            'data-closed:translate-x-full': true,
            'data-closed:-translate-x-full': mobileState.state === 'closed',
          }}
        />
      </>
    )
  }

  return <>{renderContent()}</>
}
